/**
 * Push notification subscription for game reminders.
 * - Web/PWA: Web Push (service worker + VAPID).
 * - Android APK: FCM via Capacitor Push Notifications (WebView does not support Web Push).
 */

import { apiClient } from '@/api/client';
import { getPlatform } from '@/utils/mobile';

const FCM_REGISTERED_KEY = 'mythkit_fcm_registered';

export type PushSubscribeResult =
  | { success: true }
  | { success: false; error: string };

/**
 * Subscribe to push notifications. On web uses Web Push; on Android APK uses FCM.
 */
export async function subscribeToPushNotifications(): Promise<PushSubscribeResult> {
  const platform = await getPlatform();

  if (platform === 'android') {
    return subscribeFcm();
  }

  return subscribeWebPush();
}

async function subscribeFcm(): Promise<PushSubscribeResult> {
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') {
      return { success: false, error: 'Notification permission was denied.' };
    }

    await PushNotifications.register();

    return new Promise((resolve) => {
      let settled = false;
      const finish = (result: PushSubscribeResult) => {
        if (settled) return;
        settled = true;
        resolve(result);
      };

      PushNotifications.addListener('registration', (token: { value: string }) => {
        apiClient
          .post('/api/notifications/subscribe', { fcm_token: token.value })
          .then(() => {
            if (typeof localStorage !== 'undefined') localStorage.setItem(FCM_REGISTERED_KEY, '1');
            finish({ success: true });
          })
          .catch((err: unknown) => {
            const msg = err && typeof err === 'object' && 'message' in err ? (err as { message: string }).message : String(err);
            finish({ success: false, error: msg || 'Failed to save FCM token.' });
          });
      });

      PushNotifications.addListener('registrationError', (err: { error: string }) => {
        finish({ success: false, error: err?.error || 'FCM registration failed.' });
      });

      setTimeout(() => finish({ success: false, error: 'FCM registration timed out.' }), 15000);
    });
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    return { success: false, error: message || 'Push not available on this device.' };
  }
}

async function subscribeWebPush(): Promise<PushSubscribeResult> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { success: false, error: 'Push notifications are not supported in this browser.' };
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    if (!registration) {
      return { success: false, error: 'No service worker registration. Use the app as PWA or reload.' };
    }

    const { vapidPublicKey } = await apiClient.get('/api/notifications/vapid-public-key');
    if (!vapidPublicKey) {
      return { success: false, error: 'Push notifications are not configured on the server.' };
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { success: false, error: 'Notification permission was denied.' };
    }

    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

    const payload = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: uint8ArrayToBase64(subscription.getKey('p256dh')!),
        auth: uint8ArrayToBase64(subscription.getKey('auth')!),
      },
    };

    await apiClient.post('/api/notifications/subscribe', payload);
    return { success: true };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    const apiError = err && typeof err === 'object' && 'error' in err ? (err as { error: string }).error : null;
    return { success: false, error: apiError || message || 'Failed to subscribe.' };
  }
}

/**
 * Check if push notifications are supported and permission state.
 * On Android APK, subscribed is based on local FCM registration flag or server.
 */
export async function getPushNotificationState(): Promise<{
  supported: boolean;
  permission: NotificationPermission | null;
  subscribed: boolean;
}> {
  const platform = await getPlatform();

  if (platform === 'android') {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const perm = await PushNotifications.checkPermissions();
      const permission = perm.receive === 'granted' ? 'granted' : perm.receive === 'denied' ? 'denied' : null;
      const subscribed =
        typeof localStorage !== 'undefined' && localStorage.getItem(FCM_REGISTERED_KEY) === '1';
      return { supported: true, permission, subscribed };
    } catch {
      return { supported: false, permission: null, subscribed: false };
    }
  }

  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { supported: false, permission: null, subscribed: false };
  }

  const permission = Notification.permission;
  let subscribed = false;
  try {
    const registration = await navigator.serviceWorker.ready;
    if (registration?.pushManager) {
      const sub = await registration.pushManager.getSubscription();
      subscribed = !!sub;
    }
  } catch {
    // ignore
  }

  return {
    supported: true,
    permission: permission as NotificationPermission,
    subscribed,
  };
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
